import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search as SearchIcon, X, Filter } from 'lucide-react';

const Search = ({ posts, onFilteredPosts, className = "" }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const containerRef = useRef(null);

  // Get unique categories and tags from all posts
  const { categories, allTags } = useMemo(() => {
    const categorySet = new Set();
    const tagSet = new Set();
    
    posts.forEach(post => {
      if (post.category) categorySet.add(post.category);
      if (post.tags) post.tags.forEach(tag => tagSet.add(tag));
    });
    
    return {
      categories: Array.from(categorySet).sort(),
      allTags: Array.from(tagSet).sort()
    };
  }, [posts]);

  // Close dropdown when clicking/tapping outside the component
  useEffect(() => {
    if (!showFilters) return;

    function handleOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowFilters(false);
      }
    }

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showFilters]);

  // Filter posts based on search criteria
  const filteredPosts = useMemo(() => {
    let filtered = posts;

    // Text search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(term) ||
        post.excerpt.toLowerCase().includes(term) ||
        post.category.toLowerCase().includes(term) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(term)))
      );
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Tags filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(post => 
        post.tags && selectedTags.every(tag => post.tags.includes(tag))
      );
    }

    return filtered;
  }, [posts, searchTerm, selectedCategory, selectedTags]);

  // Update parent component when filtered posts change
  React.useEffect(() => {
    onFilteredPosts(filteredPosts);
  }, [filteredPosts, onFilteredPosts]);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedCategory("");
    setSelectedTags([]);
  };

  const hasActiveFilters = searchTerm.trim() || selectedCategory || selectedTags.length > 0;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Main Search Input - Bigger */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-10 pr-10 py-3 border border-gray-600 rounded-lg bg-gray-800/50 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Filter Controls - Fixed height to prevent layout shift */}
      <div className="h-8 flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          {/* Category Filter */}
          {categories.length > 0 && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              onMouseDown={() => setShowFilters(false)}
              onTouchStart={() => setShowFilters(false)}
              onFocus={() => setShowFilters(false)}
              className="px-3 py-1.5 text-sm bg-gray-800/50 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          )}

          {/* Tags Toggle */}
          {allTags.length > 0 && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-300 bg-gray-800/50 border border-gray-600 rounded-md hover:bg-gray-700/50 transition-colors"
            >
              <Filter className="h-4 w-4" />
              Tags
              {selectedTags.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-purple-600 text-white rounded-full min-w-[1.25rem] text-center">
                  {selectedTags.length}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
            title="Clear all filters"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Results Summary - Fixed height to prevent layout shift */}
      <div className="h-4 mt-1">
        {hasActiveFilters && (
          <div className="text-xs text-gray-500">
            {filteredPosts.length} of {posts.length} posts
            {filteredPosts.length === 0 && (
              <span className="text-gray-600 ml-1">- try different criteria</span>
            )}
          </div>
        )}
      </div>

      {/* Tag Filters - Positioned to avoid covering blog titles */}
      {showFilters && allTags.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800/95 backdrop-blur-sm border border-gray-700 rounded-lg p-3 shadow-xl z-20">
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
                  selectedTags.includes(tag)
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-gray-700/60 text-gray-300 border-gray-600/50 hover:border-purple-500/50'
                }`}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;
